import type { SortBy } from '@api/inventory';

import { compareBySortBy } from './inventorySort';

/**
 * Ordering for the DoH view's Sort By control, mirroring the Snapshot's semantics
 * (`warehouse-stock-info.service.ts` compareBySortBy) against DRR-view rows.
 *
 * Flat rows are sorted by the server, which pages them; this comparator is what orders
 * the *group* rows, since grouping happens client-side. Both read the same `overall`
 * aggregate, so a group sorts by its members' combined DRR/DoH rather than by name.
 *
 * A null DoH means the row has no demand, not that it runs out today — it sinks to the
 * bottom under both LOW_STOCK and OVER_STOCK rather than leading either.
 */

/** The shape either a SKU row or a built group row exposes. */
export type DrrSortable = {
    overall: { drr: number; doh: number | null };
};

/**
 * The DoH view's binding of the shared rule (`utils/inventorySort`) to its own row shape.
 * DEFAULT returns undefined, keeping whatever order the caller already had: the server's
 * DRR-desc sort for flat rows, alphabetical for groups.
 */
export const compareDrrBySortBy = (
    sortBy: SortBy,
): ((a: DrrSortable, b: DrrSortable) => number) | undefined =>
    compareBySortBy<DrrSortable>(sortBy, {
        rateOf: (row) => row.overall.drr,
        coverOf: (row) => row.overall.doh,
    });

/** The server-side sort params that give flat rows the same ordering. */
export const drrSortParams = (
    sortBy: SortBy,
): { sortColumn?: 'sku' | 'drr' | 'doh'; sortDirection?: 'ASC' | 'DESC' } => {
    switch (sortBy) {
        case 'TOP_SELLING':
            return { sortColumn: 'drr', sortDirection: 'DESC' };
        case 'LOW_STOCK':
            return { sortColumn: 'doh', sortDirection: 'ASC' };
        case 'OVER_STOCK':
            return { sortColumn: 'doh', sortDirection: 'DESC' };
        default:
            return {};
    }
};
